import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarRange, ChevronLeft, ChevronRight, Loader2, Search, Wand2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { usePage } from "@/hooks/usePage";
import { ListPager } from "@/components/ListPager";
import { fetchAllPages } from "@/lib/paging";
import { ApiError } from "@/lib/api";
import { todayIso } from "@/lib/date-range";
import { employeesApi, type Employee } from "@/lib/employees-api";
import { rosterApi, shiftWeek, weekOf, type RosterEntry } from "@/lib/roster-api";
import { shiftsApi, type Shift } from "@/lib/shifts-api";
import { sitesApi, type SiteSummary } from "@/lib/sites-api";
import { pickedIds, siteOptions } from "@/lib/picker-options";
import { SearchMultiSelect } from "@/components/SearchMultiSelect";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Grid rows per page. The roster used to load one page of 200 employees, so
 * the rest of a 1,824-person tenant could not be rostered (2026-10-29).
 */
const PAGE_SIZE = 50;

/** The most employee ids `/roster/apply` accepts at once (ApplyRosterDto `@ArrayMaxSize(500)`). */
const APPLY_BATCH = 500;

export default function Roster() {
  const tz = useTenantTimezone();
  const { toast } = useToast();
  const [anchor, setAnchor] = useState(() => todayIso());
  // Several work locations may be picked (FEAT-2026-030); empty means all.
  const [siteFilter, setSiteFilter] = useState<string[]>([]);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [total, setTotal] = useState(0);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const siteChoices = useMemo(() => siteOptions(sites), [sites]);
  const [entries, setEntries] = useState<RosterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const days = useMemo(() => weekOf(anchor), [anchor]);
  const [bulk, setBulk] = useState({
    shiftId: "off",
    dateFrom: "",
    dateTo: "",
    onlyShiftWorkingDays: true,
    overwriteExisting: true,
  });

  /** Who the grid — and "Apply pattern" — covers. */
  const filters = useMemo(() => ({
    status: "ACTIVE" as const,
    primarySiteIds: pickedIds(siteFilter),
    search: debouncedSearch || undefined,
  }), [siteFilter, debouncedSearch]);
  // Any filter change goes back to page 1.
  const [page, setPage] = usePage(JSON.stringify(filters));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [emp, sh, st, rows] = await Promise.all([
        employeesApi.list({ ...filters, page, pageSize: PAGE_SIZE }),
        shiftsApi.list(),
        sitesApi.list({ pageSize: 200 }),
        rosterApi.list({
          dateFrom: days[0],
          dateTo: days[6],
          siteIds: pickedIds(siteFilter),
        }),
      ]);
      setEmployees(emp.data);
      setTotal(emp.total);
      setShifts(sh);
      setSites(st.data);
      setEntries(rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the roster");
    } finally {
      setLoading(false);
    }
  }, [days, siteFilter, filters, page]);

  useEffect(() => {
    void load();
  }, [load]);

  /** `employeeId|date` → entry, so a cell is a map lookup rather than a scan. */
  const byCell = useMemo(() => {
    const m = new Map<string, RosterEntry>();
    for (const e of entries) m.set(`${e.employeeId}|${e.date}`, e);
    return m;
  }, [entries]);

  const setCell = async (employeeId: string, date: string, shiftId: string | null | "clear") => {
    try {
      if (shiftId === "clear") {
        await rosterApi.clear({ employeeIds: [employeeId], dateFrom: date, dateTo: date });
      } else {
        await rosterApi.setEntry({ employeeId, date, shiftId });
      }
      await load();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not update the roster",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    }
  };

  const runApply = async () => {
    if (!bulk.dateFrom || !bulk.dateTo) {
      toast({ variant: "destructive", title: "Pick a date range" });
      return;
    }
    setApplying(true);
    let reached = 0;
    let everyone = 0;
    try {
      // Everyone the filters match, every page — not just the rows on screen —
      // sent in batches the API accepts.
      const ids = (await fetchAllPages((p, pageSize) => employeesApi.list({ ...filters, page: p, pageSize }))).map((e) => e.id);
      everyone = ids.length;
      let written = 0;
      let skipped = 0;
      for (let i = 0; i < ids.length; i += APPLY_BATCH) {
        const batch = ids.slice(i, i + APPLY_BATCH);
        const r = await rosterApi.apply({
          employeeIds: batch,
          dateFrom: bulk.dateFrom,
          dateTo: bulk.dateTo,
          shiftId: bulk.shiftId === "off" ? null : bulk.shiftId,
          onlyShiftWorkingDays: bulk.onlyShiftWorkingDays,
          overwriteExisting: bulk.overwriteExisting,
        });
        written += r.written;
        skipped += r.skipped;
        reached += batch.length;
      }
      setApplyOpen(false);
      await load();
      toast({
        title: `${written} ${written === 1 ? "day" : "days"} rostered`,
        description: skipped > 0 ? `${skipped} skipped.` : undefined,
      });
    } catch (e) {
      const reason = e instanceof ApiError ? e.message : "Something went wrong";
      toast({
        variant: "destructive",
        title: "Could not apply the pattern",
        // A failure part-way leaves earlier batches applied; say how far it got.
        description: reached > 0 ? `${reason} Applied to ${reached} of ${everyone} employees before it stopped.` : reason,
      });
      if (reached > 0) await load();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Roster</h1>
          <p className="text-sm text-muted-foreground">
            Who works which shift on which date. A blank cell means the person follows their
            usual shift; the roster only records exceptions and explicit days off.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or code…"
              aria-label="Search employees"
              className="h-9 w-48 pl-9"
            />
          </div>
          <SearchMultiSelect
            label="Work location"
            values={siteFilter}
            onChange={setSiteFilter}
            options={siteChoices}
            allLabel="All work locations"
            noun={{ one: "work location", many: "work locations" }}
            className="w-48"
          />
          <div className="flex items-center rounded-md border">
            <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Previous week"
              onClick={() => setAnchor((a) => shiftWeek(a, -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 text-xs text-muted-foreground">
              {days[0]} → {days[6]}
            </span>
            <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Next week"
              onClick={() => setAnchor((a) => shiftWeek(a, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={() => setAnchor(todayIso(tz))}>This week</Button>
          <Button
            onClick={() => {
              setBulk((b) => ({ ...b, dateFrom: days[0], dateTo: days[6] }));
              setApplyOpen(true);
            }}
          >
            <Wand2 className="mr-1.5 h-4 w-4" />
            Apply pattern
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn't load the roster</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            {error}
            <Button size="sm" variant="outline" onClick={() => void load()}>Try again</Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {loading && <Skeleton className="m-4 h-64" />}
          {!loading && employees.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">
              No active employees {debouncedSearch ? "match your search" : siteFilter.length === 0 ? "" : siteFilter.length === 1 ? "at this work location" : "at these work locations"}.
            </p>
          )}
          {!loading && employees.length > 0 && (
            // Wide content scrolls inside its own container so the page never
            // scrolls sideways.
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b bg-secondary/30">
                    <th className="sticky left-0 z-10 bg-secondary/30 p-2 text-left text-xs font-medium">
                      Employee
                    </th>
                    {days.map((d, i) => (
                      <th key={d} className="p-2 text-center text-xs font-medium">
                        <div>{DAY_LABELS[i]}</div>
                        <div className="font-normal text-muted-foreground">{d.slice(8)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} className="border-b last:border-b-0">
                      <td className="sticky left-0 z-10 max-w-[180px] truncate bg-background p-2">
                        <div className="truncate font-medium">{emp.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {emp.shiftName ?? "No default shift"}
                        </div>
                      </td>
                      {days.map((d) => {
                        const entry = byCell.get(`${emp.id}|${d}`);
                        const off = entry != null && entry.shiftId === null;
                        return (
                          <td key={d} className="p-1 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className={`w-full rounded-md border px-1.5 py-1 text-xs transition-colors ${
                                    off
                                      ? "border-border bg-secondary text-muted-foreground"
                                      : entry
                                        ? "border-primary/30 bg-primary/10 font-medium text-foreground"
                                        : "border-dashed border-border text-muted-foreground hover:text-foreground"
                                  }`}
                                  aria-label={`${emp.name} on ${d}`}
                                >
                                  {off ? "Off" : (entry?.shiftName ?? "—")}
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="center">
                                {shifts.map((s) => (
                                  <DropdownMenuItem key={s.id} onClick={() => void setCell(emp.id, d, s.id)}>
                                    {s.name}
                                  </DropdownMenuItem>
                                ))}
                                {shifts.length > 0 && <DropdownMenuSeparator />}
                                <DropdownMenuItem onClick={() => void setCell(emp.id, d, null)}>
                                  Off (explicit)
                                </DropdownMenuItem>
                                {/* Clearing is not the same as Off: with no entry the
                                    person falls back to their usual shift. */}
                                <DropdownMenuItem onClick={() => void setCell(emp.id, d, "clear")}>
                                  Clear (follow usual shift)
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ListPager page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} noun="employees" />
        </CardContent>
      </Card>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply a pattern</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert>
              <CalendarRange className="h-4 w-4" />
              <AlertDescription>
                Applies to all <strong>{total.toLocaleString()}</strong> employees matching the
                filters, on every page — not just the ones on screen. Narrow the work location
                or search first to target fewer people.
              </AlertDescription>
            </Alert>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="From">
                <Input type="date" value={bulk.dateFrom}
                  onChange={(e) => setBulk((b) => ({ ...b, dateFrom: e.target.value }))} />
              </Field>
              <Field label="To">
                <Input type="date" value={bulk.dateTo}
                  onChange={(e) => setBulk((b) => ({ ...b, dateTo: e.target.value }))} />
              </Field>
            </div>
            <Field label="Shift">
              <Select value={bulk.shiftId} onValueChange={(v) => setBulk((b) => ({ ...b, shiftId: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  <SelectItem value="off">Rostered off</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {bulk.shiftId !== "off" && (
              <label className="flex items-start gap-3">
                <Switch checked={bulk.onlyShiftWorkingDays}
                  onCheckedChange={(v) => setBulk((b) => ({ ...b, onlyShiftWorkingDays: v }))} />
                <span className="text-xs">
                  <span className="font-medium">Only the shift's own working days</span>
                  <span className="block text-muted-foreground">
                    On: dates the shift does not work are skipped. Off: every date in the range
                    is rostered, which is how you deliberately put people on outside their
                    usual pattern.
                  </span>
                </span>
              </label>
            )}
            <label className="flex items-start gap-3">
              <Switch checked={bulk.overwriteExisting}
                onCheckedChange={(v) => setBulk((b) => ({ ...b, overwriteExisting: v }))} />
              <span className="text-xs">
                <span className="font-medium">Replace existing entries</span>
                <span className="block text-muted-foreground">
                  Off: dates already rostered are left untouched, for filling gaps around
                  hand-made adjustments.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyOpen(false)} disabled={applying}>
              Cancel
            </Button>
            <Button onClick={() => void runApply()} disabled={applying}>
              {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
